import { AssetCategory, hasAssetCategoryImage } from '@simOn/asset-categories/models';
import { TransformationInterface } from '@simOn/common/matterport';
import { Attachment, AttachmentModeType, Currency, TRIGGER_FREQUENCY_LABEL } from '@simOn/common/models';
import { TScanProvider } from '@simOn/common/providers';
import { UserInfoCard } from '@simOn/common/user-info-card-models';

export type TBaseAssetInfo = {
  name: string;
  roomId: string;
  textNote?: string;
  categoryId: string | null;
  category: AssetCategory | null;
};

export interface IRoomOptions {
  id: string;
  name: string;
  color?: string;
  isMasterRoom?: boolean;
}

export interface TAsset<T = any> extends TBaseAssetInfo, AssetMonitor {
  id: string;
  hyperlinks: AssetHyperlinks[];
  events: TAssetEvent[];
  externalSource?: string | undefined;
  transformation: TransformationInterface | undefined;
  positionDeleted: boolean;
  attachedImages: Attachment[];
  attachedDocuments: Attachment[];
  maximoLink?: T | undefined;
  containsBlockedAttachments: boolean;
  brand?: string;
  model?: string;
  serialNumber?: string;
  purchasePrice?: number;
  purchaseCurrency?: Currency;
  currentValue?: number;
  currentCurrency?: Currency;
  purchaseDate?: string;
  thumbnailUrlWithSasToken?: string | null;
  warrantyEnd?: string;
}

export type TCreateAsset = Omit<
  TAsset,
  | 'id'
  | 'attachedImages'
  | 'attachedDocuments'
  | 'events'
  | 'hyperlinks'
  | 'createdAt'
  | 'author'
  | 'editedAt'
  | 'editor'
  | 'containsBlockedAttachments'
  | 'roomId'
> & {
  /** `null` leaves the asset unassigned (it lands in the space's master room). */
  roomId: string | null;
  hyperlinks: (AssetHyperlinks | Omit<AssetHyperlinks, 'id'>)[];
  events: (TAssetEvent | Omit<TAssetEvent, 'id'>)[];
  scanId?: string | undefined;
  scanTagId?: string | undefined;
  attachments: (Omit<Attachment, 'id' | 'blobPath'> & {
    id?: string | undefined | null;
    attachMode?: AttachmentModeType;
  })[];
  scanModelId?: string | undefined;
  scanProviderType?: TScanProvider | undefined;
};

export type TUpdateAsset = TCreateAsset & { id: string };

/**
 * `CreateAsset` / `UpdateAsset` respond with the asset category stripped of the image data
 * that `GetAsset` returns (`imageUrlWithSasToken`, `image`), so a custom-image category would
 * fall back to the `Other` icon on the 3D marker and in the side sheet until the asset is reloaded.
 * Fills the missing image data from the category known before the request (the picker entry or
 * the previously loaded asset category), as long as the response still points at that category.
 */
export function withKnownCategoryImage(asset: TAsset, knownCategory: AssetCategory | null | undefined): TAsset {
  if (!knownCategory || !hasAssetCategoryImage(knownCategory)) return asset;
  const category = asset.category;
  if ((category?.id ?? asset.categoryId) !== knownCategory.id) return asset;
  if (!category) return { ...asset, category: knownCategory };
  if (hasAssetCategoryImage(category)) return asset;

  return {
    ...asset,
    category: {
      ...category,
      imageId: category.imageId ?? knownCategory.imageId,
      image: category.image ?? knownCategory.image,
      imageUrlWithSasToken: knownCategory.imageUrlWithSasToken,
      scale: category.scale ?? knownCategory.scale
    }
  };
}

export function toCreateAsset(asset: TAsset): TCreateAsset {
  const {
    name,
    roomId,
    textNote,
    categoryId,
    category,
    hyperlinks,
    events,
    externalSource,
    transformation,
    positionDeleted,
    attachedImages,
    attachedDocuments,
    maximoLink: maximoLinkId,
    brand,
    model,
    serialNumber,
    purchasePrice,
    purchaseCurrency,
    currentValue,
    currentCurrency,
    purchaseDate,
    warrantyEnd
  } = asset;
  const createAsset: TCreateAsset = {
    name,
    roomId,
    textNote,
    categoryId,
    category,
    externalSource,
    transformation,
    positionDeleted,
    maximoLink: maximoLinkId,
    brand,
    model,
    serialNumber,
    purchasePrice,
    purchaseCurrency,
    currentValue,
    currentCurrency,
    purchaseDate,
    warrantyEnd,
    hyperlinks: [
      ...(hyperlinks || []).map((hyperlink) => ({
        ...hyperlink,
        id: hyperlink.id.startsWith('temp') ? null : hyperlink.id
      }))
    ],
    events: [
      ...(events || []).map((event) => ({
        ...event,
        id: event.id.startsWith('temp') ? null : event.id
      }))
    ],
    attachments: [
      ...attachedDocuments.map(
        ({ id, isBlockedBySubscriptionLimit, name, fileSizeInBytes, attachMode, hasAccess }) => ({
          id,
          isBlockedBySubscriptionLimit,
          name,
          fileSizeInBytes,
          hasAccess,
          attachMode: attachMode ?? 'FromUpload'
        })
      ),
      ...attachedImages.map(
        ({ id, isBlockedBySubscriptionLimit, name, fileSizeInBytes, isThumbnail, attachMode, hasAccess }) => ({
          id,
          isBlockedBySubscriptionLimit,
          name,
          fileSizeInBytes,
          isThumbnail,
          hasAccess,
          attachMode: attachMode ?? 'FromUpload'
        })
      )
    ]
  };
  createAsset;
  return createAsset;
}
export type TAssetEvent = IUploadEvent & { id: string };

export const RecurringEventTime = ['Day', 'Week', 'Month', 'Year'] as const;
export interface AssetHyperlinks {
  id: string;
  hyperlink: string;
}

export type TRecurringEventTime = (typeof RecurringEventTime)[number];
export const RecurringEventTimeLabel: {
  [trigger in TRecurringEventTime]: string;
} = {
  Day: TRIGGER_FREQUENCY_LABEL.EveryDay,
  Week: TRIGGER_FREQUENCY_LABEL.EveryWeek,
  Month: TRIGGER_FREQUENCY_LABEL.EveryMonth,
  Year: TRIGGER_FREQUENCY_LABEL.EveryYear
};
export interface IUploadEvent {
  dateTime: string;
  content: string;
  recurringEventType?: TRecurringEventTime | undefined;
}

export interface AssetMonitor {
  createdAt: string;
  author: UserInfoCard;
  editedAt: string | null;
  editor: UserInfoCard | null;
}

export interface DuplicateAsset {
  id: string;
  name: string;
}
